import { InjectionToken } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';

export interface CheckoutExistingParams {
  redirect_url: string;
  cancel_url: string;
  pass_thru_content: string;
  embed: boolean;
  iframe_messaging: boolean;
  subscription: {
    id: string;
    plan_id: string;
    plan_quantity: number;
    trial_end: number;
  };
  contract_term: {
    action_at_term_end: string;
  };
}

export interface IdTokenPayload {
  at_hash: string;
  aud: string;
  email: string;
  email_verified: string;
  exp: number;
  iat: number;
  iss: string;
  name: string;
  nickname: string;
  nonce: string;
  picture: string;
  sub: string;
  updated_at: string;
}

export interface AuthResult {
  accessToken: string;
  appState: string;
  expiresIn: number;
  idToken: string;
  idTokenPayload: IdTokenPayload;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshToken: any;
  scope: string;
  state: string;
  tokenType: string;
}

export const LOGIN_MESSAGES = {
  unauthorized: extractTranslation('login.messages.unauthorized'),
  forbidden: extractTranslation('login.messages.forbidden'),
  forceLogout: extractTranslation('login.messages.force_logout'),
  urlLogout: extractTranslation('login.messages.url_logout'),
  userLogout: extractTranslation('login.messages.user_logout'),
  wrongCustomer: extractTranslation('login.messages.wrong_customer'),
  missingCustomer: extractTranslation('login.messages.missing_customer'),
  enterpriseConnectionAttemptError: extractTranslation('login.messages.enterprise_connection_attempt_error'),
  custom: extractTranslation('login.messages.custom'),
} as const;

export function isLoginMessage(item: string): item is keyof typeof LOGIN_MESSAGES {
  return !!item && Object.keys(LOGIN_MESSAGES).includes(item);
}

export enum AuthStatus {
  Authenticated = 'AUTHENTICATED',
  Unauthenticated = 'UNAUTHENTICATED',
  Error = 'ERROR',
  VerifyEmail = 'VERIFY_EMAIL',
  UnknownTenantId = 'UNKNOWN_MS_TENANT_ID',
}

export class AuthInfo {
  constructor(
    public status: AuthStatus,
    public expiresAt: string | null,
    public createdAt: string | null,
    public accessToken: string | null,
    public idToken: string | null,
    public idTokenPayload?: IdTokenPayload | null,
    public message?: string,
  ) {}

  private static _isValid(expiresAt: string, status: string): boolean {
    return !AuthInfo._hasExpired(expiresAt) && status === AuthStatus.Authenticated;
  }

  private static _hasExpired(expiresAt: string): boolean {
    return new Date().getTime() > JSON.parse(expiresAt);
  }

  public static createError(message?: string): AuthInfo {
    let error = AuthStatus.Error;
    if (message === 'VERIFY_EMAIL') {
      error = AuthStatus.VerifyEmail; // TODO: Remove this one;
    } else if (message === 'UNKNOWN_MS_TENANT_ID') {
      error = AuthStatus.UnknownTenantId;
    }
    return new AuthInfo(error, null, null, null, null, null, message || 'An error occurred.');
  }

  public static createUnauthenticated(message?: string): AuthInfo {
    return new AuthInfo(
      AuthStatus.Unauthenticated,
      null,
      null,
      null,
      null,
      null,
      message || 'User is not authenticated yet.',
    );
  }

  public static createAuthenticated(
    expiresAt: string,
    createdAt: string,
    accessToken: string,
    idToken: string,
    idTokenPayload: IdTokenPayload,
    message?: string,
  ): AuthInfo {
    return new AuthInfo(
      AuthStatus.Authenticated,
      expiresAt,
      createdAt,
      accessToken,
      idToken,
      idTokenPayload,
      message || 'User is authenticated.',
    );
  }

  public isValid(): boolean {
    return AuthInfo._isValid(String(this.expiresAt), this.status);
  }

  public hasExpired(): boolean {
    return AuthInfo._hasExpired(String(this.expiresAt));
  }

  public isOlderThan(age: number): boolean {
    const createdAt: unknown = JSON.parse(String(this.createdAt));
    const date = typeof createdAt === 'number' ? createdAt + age : -1;
    return new Date().getTime() > date;
  }
}

export const APP_AUTH_CONFIG_TOKEN = new InjectionToken<AppAuthConfig>('AppAuthConfig');
export interface AppAuthConfig {
  domain: string;
  clientId: string;
  responseType: string;
  audience: string;
  scope: string;
}
